import React, { useState, useMemo, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation, Navigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextLazy';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { FeatureGate } from './FeatureGate';
import { Dashboard } from '../../pages/portal/Dashboard';
import { Projects } from '../../pages/portal/Projects';
import { ProjectDetail } from '../../pages/portal/ProjectDetail';
import { EngagementsPipeline } from '../../pages/portal/EngagementsPipeline';
import { EngagementDetail } from '../../pages/portal/EngagementDetail';
import { NewEngagement } from '../../pages/portal/NewEngagement';
import { Clients } from '../../pages/portal/Clients';
import { ClientDetail } from '../../pages/portal/clients/[id]';
import { NewClient } from '../../pages/portal/NewClient';
import { Settings } from '../../pages/portal/Settings';
import SecuritySettings from '../../pages/portal/SecuritySettings';
import { Documents } from '../../pages/portal/Documents';
import AdvisorMessagesV2 from '../../pages/portal/MessagesV2';
import FeatureAdminPage from '../../pages/portal/admin/features';
import Users from '../../pages/portal/admin/Users';
import { Integrations } from '../../pages/portal/admin/Integrations';
import { MyMeetingsIntegration } from '../../pages/portal/admin/integrations/MyMeetings';
import { ClientMapping } from '../../pages/portal/admin/integrations/mymeetings/ClientMapping';
import { MyMeetingsConnect } from '../../pages/portal/admin/integrations/mymeetings/Connect';
import { SMSTemplatesAdmin } from '../../pages/portal/admin/sms-templates';
import { ProjectTemplates } from '../../pages/portal/admin/ProjectTemplates';
import { LogoutConfirmationModal } from './LogoutConfirmationModal';
import { NotificationCenter } from './NotificationCenter';
import Notifications from '../../pages/portal/Notifications';
import { colors } from '../../lib/colors';
import { invitationNotificationService } from '../../services/invitationNotificationService';
import { portalFeatures, isNavigationItemVisible } from '../../config/portalFeatureFlags';
import { Logo } from '../common/Logo';

// Compliance imports
import ComplianceWizard from '../../pages/portal/compliance/ComplianceWizard';
import NewDocument from '../../pages/portal/compliance/NewDocument';
import NeedsAnalysisWizard from '../../pages/portal/compliance/wizards/NeedsAnalysisWizard';
import ReasonWhyBuilder from '../../pages/portal/compliance/wizards/ReasonWhyBuilder';

// Client Portal imports
import ClientPortalDashboard from '../../pages/portal/client/ClientPortalDashboard';
import ClientEngagementView from '../../pages/portal/client/ClientEngagementView';

import {
  HomeIcon,
  DocumentTextIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  LinkIcon,
  ArrowLeftIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  BellIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  roles?: string[];
  hideFromRoles?: string[];  // Hide from these roles (opposite of roles)
  feature?: string;
  key?: string;
}

const baseNavigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/portal/dashboard', icon: HomeIcon, feature: 'dashboard', key: 'dashboard' },
  { name: 'My Projects', href: '/portal/client/dashboard', icon: FolderIcon, hideFromRoles: ['pcm_staff', 'super_admin'] },  // Client portal dashboard
  { name: 'Clients', href: '/portal/clients', icon: UserGroupIcon, roles: ['pcm_staff', 'super_admin'], key: 'clients' },
  { name: 'Engagements', href: '/portal/engagements', icon: ViewColumnsIcon, roles: ['pcm_staff', 'super_admin'], key: 'engagements' },
  { name: 'Notifications', href: '/portal/notifications', icon: BellIcon },
  { name: 'Compliance', href: '/portal/compliance', icon: ClipboardDocumentCheckIcon, roles: ['pcm_staff', 'super_admin'] },
  { name: 'Projects', href: '/portal/projects', icon: FolderIcon, key: 'projects', hideFromRoles: ['pcm_staff', 'super_admin'] },  // Legacy - clients only
  { name: 'Documents', href: '/portal/documents', icon: DocumentTextIcon, feature: 'document_vault', key: 'documents' },
  { name: 'Messages', href: '/portal/messages', icon: ChatBubbleLeftRightIcon, feature: 'messaging', key: 'messages' },
  { name: 'Support', href: '/portal/support', icon: TicketIcon, feature: 'client_requests', key: 'support' },
  { name: 'Analytics', href: '/portal/analytics', icon: ChartBarIcon, roles: ['client_admin', 'pcm_staff', 'super_admin'], key: 'analytics' },
  { name: 'Settings', href: '/portal/settings', icon: Cog6ToothIcon, key: 'settings' },
  { name: 'Users', href: '/portal/admin/users', icon: UserGroupIcon, roles: ['super_admin'] },
  { name: 'Integrations', href: '/portal/admin/integrations', icon: LinkIcon, roles: ['pcm_staff', 'super_admin'] },
  { name: 'SMS Templates', href: '/portal/admin/sms-templates', icon: ClipboardDocumentListIcon, roles: ['pcm_staff', 'super_admin'] },
  { name: 'Feature Management', href: '/portal/admin/features', icon: WrenchScrewdriverIcon, roles: ['super_admin'] }
]

// Page title mapping
const pageTitles: Record<string, { title: string; description?: string }> = {
  '/portal/dashboard': { title: 'Dashboard', description: 'Monitor your portal activity and metrics' },
  '/portal/clients': { title: 'Clients', description: 'Manage your client relationships and information' },
  '/portal/clients/new': { title: 'New Client', description: 'Add a new client to your portfolio' },
  '/portal/compliance': { title: 'Compliance', description: 'Manage compliance documents and workflows' },
  '/portal/compliance/new': { title: 'New Document', description: 'Create a new compliance document' },
  '/portal/projects': { title: 'Projects', description: 'Track and manage your projects' },
  '/portal/projects/new': { title: 'New Engagement', description: 'Create a new client engagement' },
  '/portal/projects/:id': { title: 'Project Details', description: 'View and manage project progress' },
  '/portal/engagements': { title: 'Engagements Pipeline', description: 'Kanban view of all engagements by status' },
  '/portal/engagements/:id': { title: 'Engagement Details', description: 'View and manage engagement progress' },
  '/portal/documents': { title: 'Documents', description: 'Access and manage your documents' },
  '/portal/messages': { title: 'Messages', description: 'Communicate with your team' },
  '/portal/support': { title: 'Support', description: 'Get help and support' },
  '/portal/analytics': { title: 'Analytics', description: 'View insights and reports' },
  '/portal/settings': { title: 'Settings', description: 'Manage your account settings' },
  '/portal/admin/users': { title: 'Users', description: 'Manage user accounts and permissions' },
  '/portal/admin/features': { title: 'Feature Management', description: 'Control feature flags and settings' },
  '/portal/admin/integrations': { title: 'Integrations', description: 'Manage third-party integrations' },
  '/portal/admin/sms-templates': { title: 'SMS Templates', description: 'Manage message templates and quick replies' },
  '/portal/admin/project-templates': { title: 'Project Templates', description: 'Manage reusable project workflows and templates' },
  '/portal/notifications': { title: 'Notifications', description: 'View and manage your notifications' },
  // Client Portal pages
  '/portal/client/dashboard': { title: 'My Projects', description: 'View your projects and tasks' },
  '/portal/client/projects/:id': { title: 'Project Details', description: 'View your project progress' },
};

export const PortalLayoutWithFeatures: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const { userProfile, organization, signOut, hasRole, user } = useAuth()
  const { isFeatureEnabled } = useFeatureFlags()
  const navigate = useNavigate()
  const location = useLocation()

  // Hide header for wizard routes
  const isWizardRoute = location.pathname.includes('/compliance/wizard/')

  // Get current page title
  const getPageTitle = () => {
    // Check for exact match first
    if (pageTitles[location.pathname]) {
      return pageTitles[location.pathname];
    }

    // Check for pattern matches (e.g., /portal/clients/:id)
    if (location.pathname.match(/^\/portal\/clients\/[^/]+$/)) {
      return { title: 'Client Details', description: 'View and manage client information' };
    }

    // Check for project detail pages
    if (location.pathname.match(/^\/portal\/projects\/[^/]+$/)) {
      return { title: 'Project Details', description: 'View and manage project progress' };
    }

    // Check for engagement detail pages
    if (location.pathname.match(/^\/portal\/engagements\/[^/]+$/)) {
      return { title: 'Engagement Details', description: 'View and manage engagement progress' };
    }

    // Check for client portal project pages
    if (location.pathname.match(/^\/portal\/client\/projects\/[^/]+$/)) {
      return { title: 'Project Details', description: 'View your project progress' };
    }

    // Default fallback
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return {
      title: lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' '),
      description: ''
    };
  };

  const currentPage = getPageTitle();

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Failed to log out:', error)
    }
  }

  // Filter navigation based on roles AND features
  const filteredNavigation = useMemo(() => {
    return baseNavigation.filter(item => {
      // First check our custom portal feature flags
      if (item.key && !isNavigationItemVisible(item.key)) {
        return false
      }

      // Check if item should be hidden from user's role
      if (item.hideFromRoles && hasRole(item.hideFromRoles)) {
        return false
      }

      // Check role access
      if (item.roles && !hasRole(item.roles)) {
        return false
      }

      // Check feature access (from database feature flags)
      if (item.feature && !isFeatureEnabled(item.feature as any)) {
        return false
      }

      return true
    })
  }, [hasRole, isFeatureEnabled, userProfile?.role])

  // Subscribe to invitation notifications for advisors
  useEffect(() => {
    if (!userProfile?.id || !hasRole(['pcm_staff', 'super_admin'])) return;

    // Subscribe to all invitations for this advisor
    const channel = invitationNotificationService.subscribeToAllInvitations(
      userProfile.id,
      async (notification) => {
        // Store notification in database
        await invitationNotificationService.storeNotification(notification);

        // The NotificationCenter will pick up the new notification automatically
      }
    );

    return () => {
      invitationNotificationService.unsubscribe(channel);
    };
  }, [userProfile?.id, hasRole]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-16 flex items-center px-6 border-b border-gray-100">
            <Logo className="h-8 w-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={`mr-3 h-5 w-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                      aria-hidden="true"
                    />
                    {item.name}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User Profile & Logout */}
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center p-2 rounded-lg mb-2">
              <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                {userProfile?.full_name?.[0] || user?.email?.[0] || 'U'}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userProfile?.full_name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {organization?.name || user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutConfirmation(true)}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-red-500" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        {!isWizardRoute && location.pathname !== '/portal/messages' && !location.pathname.startsWith('/portal/messages/') && (
          <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 shadow-sm z-10">
            <div className="flex items-center">
              <button
                type="button"
                className="lg:hidden -ml-2 mr-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Page Title */}
              <div className="flex flex-col justify-center ml-2">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{currentPage.title}</h1>
                {currentPage.description && (
                  <p className="text-xs text-gray-500 hidden sm:block">{currentPage.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search Bar (Optional) */}
              <div className="hidden md:flex items-center max-w-xs">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                    placeholder="Search..."
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <NotificationCenter />
                <button className="p-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <span className="sr-only">Help</span>
                  <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                {/* Logged in user */}
                <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-gray-200">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                    {userProfile?.full_name?.[0] || user?.email?.[0] || 'U'}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                      {userProfile?.full_name || 'User'}
                    </span>
                    <span className="text-xs text-gray-500 truncate max-w-[120px]">
                      {userProfile?.role === 'super_admin' ? 'Admin' : userProfile?.role === 'pcm_staff' ? 'Staff' : 'User'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 focus:outline-none">
          <Routes>
            <Route path="/" element={<Navigate to="/portal/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/new" element={<NewClient />} />
            <Route path="/clients/:id" element={<ClientDetail />} />

            {/* Notifications */}
            <Route path="/notifications" element={<Notifications />} />

            {/* Compliance routes */}
            <Route path="/compliance" element={<ComplianceWizard />} />
            <Route path="/compliance/new" element={<NewDocument />} />
            <Route path="/compliance/wizard/needs_analysis" element={<NeedsAnalysisWizard />} />
            <Route path="/compliance/wizard/reason_why" element={<ReasonWhyBuilder />} />
            <Route path="/compliance/edit/:documentId" element={<NeedsAnalysisWizard />} />
            <Route path="/compliance/reason-why/edit/:documentId" element={<ReasonWhyBuilder />} />

            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/new" element={<NewEngagement />} />
            <Route path="/projects/:projectId" element={<ProjectDetail />} />
            <Route path="/engagements" element={<EngagementsPipeline />} />
            <Route path="/engagements/:id" element={<EngagementDetail />} />

            {/* Client Portal routes */}
            <Route path="/client/dashboard" element={<ClientPortalDashboard />} />
            <Route path="/client/projects/:id" element={<ClientEngagementView />} />

            {/* Feature-gated routes */}
            <Route path="/documents" element={
              <FeatureGate feature="document_vault" fallback={<div className="p-6">Documents feature is not enabled</div>}>
                <Documents />
              </FeatureGate>
            } />

            <Route path="/messages" element={
              <FeatureGate feature="messaging" fallback={<div className="p-6">Messaging feature is not enabled</div>}>
                <AdvisorMessagesV2 />
              </FeatureGate>
            } />
            <Route path="/messages/:threadId" element={
              <FeatureGate feature="messaging" fallback={<div className="p-6">Messaging feature is not enabled</div>}>
                <AdvisorMessagesV2 />
              </FeatureGate>
            } />

            <Route path="/support" element={
              <FeatureGate feature="client_requests" fallback={<div className="p-6">Support feature is not enabled</div>}>
                <div className="p-6">Support coming soon</div>
              </FeatureGate>
            } />

            <Route path="/analytics" element={<div className="p-6">Analytics coming soon</div>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/security" element={<SecuritySettings />} />
            <Route path="/admin/users" element={<Users />} />
            <Route path="/admin/features" element={<FeatureAdminPage />} />
            <Route path="/admin/integrations" element={<Integrations />} />
            <Route path="/admin/integrations/mymeetings" element={<MyMeetingsIntegration />} />
            <Route path="/admin/integrations/mymeetings/mapping" element={<ClientMapping />} />
            <Route path="/admin/integrations/mymeetings/connect" element={<MyMeetingsConnect />} />
            <Route path="/admin/sms-templates" element={<SMSTemplatesAdmin />} />
            <Route path="/admin/project-templates" element={<ProjectTemplates />} />
          </Routes>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutConfirmation}
        onConfirm={handleSignOut}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
    </div>
  )
}